import { ReactNode, createContext, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Nav, Table } from 'react-bootstrap';
import { exceptionsService } from '@/services/api';
import { useAsync } from '@/common/hooks/use-async';
import { Flow, Layers, Timeline } from './diagrams';

/** In-app user guide. Each tab covers one area of the console: what it is, how it behaves, and
 *  why it exists. Keep it in sync with docs/USER-GUIDE.md (the long-form written source) and
 *  with the numbers the API enforces (90-day window, 14-day warning). */

// The staleness rule is fetched from the API (GET /exceptions/policy) so this page quotes the
// numbers the server enforces. These defaults only render until it loads, or if it fails.
const FALLBACK_POLICY = { expiryDays: 90, warnDays: 14 };
const PolicyContext = createContext(FALLBACK_POLICY);
const usePolicy = () => useContext(PolicyContext);

function Why({ children }: { children: ReactNode }) {
  return (
    <Alert variant="light" className="border-start border-4 border-primary small py-2 my-3">
      <strong>Why it exists. </strong>
      {children}
    </Alert>
  );
}

function Term({ name, children }: { name: string; children: ReactNode }) {
  return (
    <tr>
      <th scope="row" className="text-nowrap align-top small" style={{ width: '14rem' }}>
        {name}
      </th>
      <td className="small">{children}</td>
    </tr>
  );
}

/* ------------------------------------------------------------------ Overview */
function Overview() {
  return (
    <>
      <p>
        vuln-sentinel watches AWS Inspector for vulnerabilities on your onboarded resources (ECR
        images and Lambda functions). For each one it opens a <strong>pull request</strong> that
        fixes the vulnerable dependency, or, when no safe code fix exists, tracks a compliance{' '}
        <strong>exception</strong>. A human always reviews and merges. It never merges on its own.
      </p>
      <Flow
        label="End-to-end flow"
        nodes={[
          { title: 'AWS Inspector', sub: 'emits a finding', tone: 'secondary' },
          { title: 'Receiver', sub: 'resolves repo, dedups, suppresses', tone: 'primary' },
          { title: 'Coalescer', sub: 'groups per repo', tone: 'primary' },
          { title: 'Agent', sub: 'decides fix or exception', tone: 'info' },
          { title: 'Draft PR', sub: 'you review and merge', tone: 'success' },
        ]}
        caption="Pull latest findings feeds the same pipeline on demand."
      />
      <Why>
        The goal is to turn “a scanner found a vuln” into “here is a PR that fixes it”, so your team
        spends its time reviewing a fix instead of triaging a finding.
      </Why>
      <h5 className="mt-4">The console at a glance</h5>
      <Table size="sm" bordered>
        <tbody>
          <Term name="Repos">
            Every onboarded repo with open-vulnerability counts by severity. Click one to open its
            detail page (Configuration, Findings, Learned context, Activity).
          </Term>
          <Term name="Onboard">Wizard to add a Bitbucket repo and point it at its AWS stacks.</Term>
          <Term name="PRs">Every fix PR vuln-sentinel opened, with status and feedback.</Term>
          <Term name="Exceptions">Exception records: drafted by the agent or recorded from ServiceNow.</Term>
          <Term name="Activity">Audit log of every state-changing action.</Term>
        </tbody>
      </Table>
      <h5 className="mt-4">Guardrails that never change</h5>
      <ul className="small">
        <li>A human merges. vuln-sentinel proposes, you decide.</li>
        <li>Lockfiles are never edited (you reconcile them, see Pull requests).</li>
        <li>It never pushes to your default branch.</li>
        <li>It never files, amends or closes anything in ServiceNow.</li>
        <li>Feedback can tighten behavior, never relax a guardrail.</li>
      </ul>
    </>
  );
}

/* ------------------------------------------------------------------ Repos */
function Repos() {
  return (
    <>
      <h5>Repos list</h5>
      <p>
        One row per onboarded repo with open-vulnerability counts in <strong>Crit / High / Med /
        Low</strong> columns and a total. It is sorted most-urgent first and sortable by any
        column. Clicking a repo opens its Findings tab.
      </p>
      <p>
        Counts are <strong>distinct CVEs on the latest image build</strong>, net of anything covered
        by a valid exception or skipped (repo disabled, policy off, below the severity floor).
        Those are already known or accepted, so they are not “open”. The number matches the
        <em> Needs action</em> filter on the repo’s Findings tab exactly.
      </p>
      <Why>
        Inspector repeats one CVE for every image build, so raw counts were wildly inflated (one repo
        showed 551 for 40 real CVEs). Counting distinct CVEs on the current build is the only number
        a team can act on. The counts are computed by a scheduled job (<strong>every 15
        minutes</strong>) and stored on the repo row, so the list loads instantly instead of
        scanning on every page view. They can therefore lag a repo’s Findings tab by up to 15
        minutes, and they only change as Inspector reports new scans. To refresh one repo
        immediately, open it and use <strong>Pull latest findings</strong> on its Findings tab.
      </Why>

      <h5 className="mt-4">Onboarding a repo</h5>
      <Flow
        label="Onboarding steps"
        nodes={[
          { title: 'Pick repo', sub: 'Bitbucket workspace/repo', tone: 'primary' },
          { title: 'Pick stacks', sub: 'region(s) + CloudFormation', tone: 'primary' },
          { title: 'Behavior', sub: 'branch, owners, AI context', tone: 'primary' },
          { title: 'Review resources', sub: 'discovered live', tone: 'info' },
          { title: 'Save', sub: 'then findings are pulled', tone: 'success' },
        ]}
      />
      <p>
        You give vuln-sentinel the app’s CloudFormation stack(s). It reads the resource list (ECR
        images, Lambda functions) live from them, now and on a daily sweep.
      </p>
      <Why>
        The hard-to-derive fact (which stacks make up the app) is the one thing we ask for. The thing
        that changes (the resources) is derived. When the team adds a Lambda to the stack later,
        vuln-sentinel picks it up without re-onboarding. EC2/EKS findings are out of scope for now.
        Their fix is usually an AMI or patch, not a code PR.
      </Why>

      <h5 className="mt-4">Configuration tab</h5>
      <Table size="sm" bordered>
        <tbody>
          <Term name="Enabled">The pause switch. Off keeps all config but stops vuln-sentinel acting on findings.</Term>
          <Term name="Monitored resources">The ECR images and Lambdas watched. Auto-discovered from stacks, editable.</Term>
          <Term name="Branches">Default branch (what fixes target) and the fix/exception branch-name patterns.</Term>
          <Term name="AI context root">
            Where your repo keeps security context for the agent (default <code>ai_context</code>).
            A repo with none still works; the agent uses global guidance.
          </Term>
          <Term name="Owners">Who is emailed about this repo’s PRs and exception aging.</Term>
        </tbody>
      </Table>
      <Why>
        There is no “remediation policy” or “validation mode” control. Remediation is always{' '}
        <em>assist</em> (draft PRs, human merges) and CI validation is always on, so those switches
        would imply choices that do not exist.
      </Why>
    </>
  );
}

/* ------------------------------------------------------------------ Findings */
function Findings() {
  const { expiryDays: EXPIRY_DAYS } = usePolicy();
  return (
    <>
      <p>
        The Findings tab answers one question: <em>which vulnerabilities in this repo still need a
        decision?</em> It has two views of the same data (<strong>By CVE</strong> and{' '}
        <strong>By REQ</strong>), a coverage filter and an image selector.
      </p>

      <h5>By CVE</h5>
      <p>
        One row per <strong>distinct CVE</strong>: severity, CVE, package, the image(s) affected and
        its ServiceNow <strong>REQ</strong> with an aging badge if it has an exception. Expand a row
        to see the underlying Inspector findings per image build.
      </p>
      <Why>
        Inspector reports a CVE separately for every image digest, so the raw feed repeats one vuln
        dozens of times. The server collapses it. There is no “status” or “installed/fixed version”
        column on purpose. The internal pipeline status only records hand-off to the agent and would
        contradict the REQ column, and the exception state is what tells you whether a CVE needs
        action.
      </Why>

      <h5 className="mt-4">Coverage filter</h5>
      <Table size="sm" bordered>
        <tbody>
          <Term name="Needs action (default)">
            CVEs with no <em>valid</em> exception: none at all, or one whose {EXPIRY_DAYS}-day
            window has lapsed (shown as <em>expired — re-evaluate</em>). Same set the repos list
            counts.
          </Term>
          <Term name="Covered by exception">CVEs under an unexpired filed or approved exception.</Term>
          <Term name="All">Both.</Term>
        </tbody>
      </Table>

      <h5 className="mt-4">Images</h5>
      <p>
        Only the <strong>current build</strong> of each image is shown. When a newer build is
        scanned, older builds’ findings are marked <code>SUPERSEDED</code> and drop out. “Current”
        is decided by the image’s <strong>push time</strong> in ECR, not by which finding Inspector
        reported last, because Inspector keeps re-reporting old images that are still in ECR.
      </p>
      <p>
        <strong>All images</strong> dedupes by CVE across images. Picking one image gives that
        image’s own count.
      </p>
      <Why>
        Whether two images share the same vulns (for example built from one Dockerfile) is a call
        only the owning team can make, so the UI shows both views instead of assuming.
      </Why>

      <h5 className="mt-4">How fresh is this data?</h5>
      <Flow
        label="How a pushed image reaches the Findings tab"
        nodes={[
          { title: 'You push an image', tone: 'secondary' },
          { title: 'Inspector scans it', sub: 'timing is AWS’s', tone: 'warning' },
          { title: 'Finding is emitted', sub: 'vuln-sentinel receives it', tone: 'primary' },
          { title: 'Old build retired', sub: 'findings view updates', tone: 'success' },
          { title: 'Repos-list totals', sub: 'recalculated every 15 min', tone: 'info' },
        ]}
      />
      <ul className="small">
        <li>
          vuln-sentinel <strong>does not poll</strong> Inspector on a schedule. It reacts to findings
          as Inspector emits them.
        </li>
        <li>
          So after you push a fixed image, the old CVEs <strong>do not disappear immediately</strong>.
          They are retired once the new build has been scanned and its findings received. How long
          that takes is up to Inspector, not vuln-sentinel.
        </li>
        <li>
          The per-severity totals on the Repos list are recalculated every <strong>15 minutes</strong>,
          so they can lag the Findings tab by up to that long.
        </li>
        <li>
          A finding goes away in one of three ways: its image build is <strong>superseded</strong> by
          a newer build, Inspector reports it <strong>closed</strong> (the vuln was remediated), or
          the daily <strong>reconcile</strong> notices Inspector no longer lists it as active. The
          reconcile also runs whenever you click <strong>Pull latest findings</strong>, and it is the
          one that clears fixed Lambda functions (they have no image build to supersede). It only
          resolves what Inspector itself stopped listing, never on age alone.
        </li>
      </ul>
      <Why>
        Inspector already decides when to scan and report. Polling it on our own timer would add load
        and cost for no earlier answer, and still could not make Inspector scan sooner. What we can
        do is make the lag visible and give you a way to refresh one repo on demand.
      </Why>

      <h5 className="mt-4">Pull latest findings</h5>
      <Flow
        label="Pull latest findings"
        nodes={[
          { title: 'Button', sub: 'or automatic after onboard', tone: 'primary' },
          { title: 'Inspector ListFindings', sub: 'read-only, ACTIVE only', tone: 'secondary' },
          { title: 'Same ingress queue', sub: 'dedup, suppression', tone: 'primary' },
          { title: 'Normal PRs', tone: 'success' },
        ]}
      />
      <p>
        It re-reads the resources’ current ACTIVE findings and pushes them through the{' '}
        <em>same</em> pipeline the event path uses. It is safe to press repeatedly: dedup means
        nothing already handled opens a duplicate PR. It also works in reverse: any open finding
        Inspector no longer lists as active is marked <strong>resolved</strong> and drops out of
        the list, and the result message says how many.
      </p>
      <p>
        <strong>It works one repo at a time.</strong> Open the repo (Repos list → click the repo →{' '}
        <strong>Findings</strong> tab) and click <strong>Pull latest findings</strong>. There is no
        “pull everything” button. Use it right after onboarding, after pushing an image once
        Inspector has scanned it, or when your pipeline is blocked by the very vulnerabilities a
        redeploy would scan. It runs automatically once at onboarding. It can only pull what
        Inspector currently reports, so if the new image has not been scanned yet, wait and try
        again.
      </p>
      <Why>
        Inspector pushes a finding only when it emits one, never retroactively for a subscriber that
        was not listening yet. A newly onboarded long-lived service would look deceptively clean
        until its next deploy, and redeploying to force a scan is exactly what the pipeline’s own
        vulnerability gate often blocks.
      </Why>
    </>
  );
}

/* ------------------------------------------------------------------ Exceptions & REQs */
function Exceptions() {
  const { expiryDays: EXPIRY_DAYS, warnDays: WARN_DAYS } = usePolicy();
  return (
    <>
      <p>
        When a vuln has no safe code fix, the right answer is a documented, time-boxed acceptance of
        the risk: a ServiceNow <strong>REQ</strong>. vuln-sentinel does <strong>not</strong> file
        REQs. It records the ones your team files so Inspector re-reporting the CVE stops creating
        noise, and it tells you when one goes stale.
      </p>

      <h5>When does an exception go stale?</h5>
      <Timeline expiryDays={EXPIRY_DAYS} warnDays={WARN_DAYS} />
      <ul className="small">
        <li>
          A REQ covers its CVEs for <strong>{EXPIRY_DAYS} days (about 3 months) from its filed
          date</strong>.
        </li>
        <li>
          In the last <strong>{WARN_DAYS} days</strong> the repo owners get a heads-up email and the
          badge turns to <em>Nd left</em>.
        </li>
        <li>
          At day {EXPIRY_DAYS} the CVE goes back to <strong>Needs action</strong> (<em>expired Nd
          ago</em>) and a second email confirms it. The next report of the CVE re-opens remediation.
        </li>
      </ul>
      <Why>
        ServiceNow policy: exceptions are re-evaluated every 90 days from submission, are never
        amended, and a new CVE needs a new REQ. The console mirrors that rule instead of inventing
        its own, so what you see matches what auditors expect.
      </Why>

      <h5 className="mt-4">REQ lifecycle</h5>
      <Flow
        label="REQ lifecycle"
        nodes={[
          { title: 'Attach REQ', sub: 'per CVE, with filed date', tone: 'primary' },
          { title: 'Covered', sub: 'CVE suppressed', tone: 'success' },
          { title: 'Expiring', sub: `last ${WARN_DAYS} days`, tone: 'warning' },
          { title: 'Expired', sub: 'back in Needs action', tone: 'danger' },
          { title: 'Renew', sub: 'new REQ, fresh clock', tone: 'primary' },
        ]}
      />

      <h5 className="mt-4">The actions, and when each is offered</h5>
      <Table size="sm" bordered>
        <thead>
          <tr>
            <th>Action</th>
            <th>Where</th>
            <th>What it does</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="small fw-semibold">Attach REQ</td>
            <td className="small">By CVE, on an uncovered CVE</td>
            <td className="small">
              Records a REQ your team already filed (number + filed date). The CVE becomes{' '}
              <em>covered</em> and no fix PR is opened. If it was filed over {EXPIRY_DAYS} days ago,
              attach it with the real date anyway: it shows as expired, which is the accurate
              picture.
            </td>
          </tr>
          <tr>
            <td className="small fw-semibold">Remove REQ</td>
            <td className="small">By CVE, on a covered CVE</td>
            <td className="small">
              Direct undo of Attach, for a REQ put on the wrong CVE. Only that CVE returns to Needs
              action; the REQ’s other CVEs stay covered.
            </td>
          </tr>
          <tr>
            <td className="small fw-semibold">Edit / Set REQ #</td>
            <td className="small">By REQ, on a live REQ</td>
            <td className="small">
              Renames the <em>same ticket</em> in place, typically replacing an <code>RITM</code> id
              with the <code>REQ</code> ServiceNow assigned later, or fixing a typo. Filed date and
              expiry do not move. Not offered on an expired REQ.
            </td>
          </tr>
          <tr>
            <td className="small fw-semibold">Renew…</td>
            <td className="small">By REQ, when expired or in the last {WARN_DAYS} days</td>
            <td className="small">
              A <em>different</em> ticket. File a new REQ in ServiceNow first, then enter its number
              and filed date. CVEs still on the image move to it with a fresh {EXPIRY_DAYS}-day
              clock; the old REQ is retired. CVEs that already <strong>cleared</strong> are not
              carried over.
            </td>
          </tr>
          <tr>
            <td className="small fw-semibold">Retire…</td>
            <td className="small">By REQ, only when every CVE has cleared</td>
            <td className="small">
              Drops a REQ with nothing left to cover. Hidden while any CVE is still present, so you
              cannot accidentally uncover something.
            </td>
          </tr>
        </tbody>
      </Table>
      <Why>
        Edit and Renew are separate because they are different facts. Renaming a ticket must not
        reset its clock (that would let an old exception look new), and a new ticket must not inherit
        the old date. Nothing here touches ServiceNow. Retired rows are kept {EXPIRY_DAYS} days as an
        audit trail, then removed automatically.
      </Why>

      <h5 className="mt-4">By REQ view</h5>
      <p>
        Lists the repo’s filed REQs, soonest-to-expire first: filed date, re-evaluate-by date with a
        badge, and the CVEs inside each marked <strong>present</strong> (Inspector still reports it)
        or <strong>cleared</strong> (gone from the image, a candidate to retire in your exception
        docs). A REQ with only an <code>RITM</code> id shows <em>RITM — REQ # pending</em> until you
        set the REQ number.
      </p>
      <Why>
        The REQ is the unit people actually renew: one ticket, one expiry, many CVEs. It gets its own
        view rather than being repeated on every CVE row.
      </Why>

      <h5 className="mt-4">Exceptions page</h5>
      <p>
        Lists exception records (drafted by the agent for a component, or recorded from ServiceNow)
        with their state: Drafted, Submitted, Approved, Rejected, Withdrawn, Expired, Retired.
        Exceptions are grouped <strong>per component</strong>, since one request covers all of that
        component’s CVEs and the engineering response is identical.
      </p>
    </>
  );
}

/* ------------------------------------------------------------------ PRs */
function Prs() {
  return (
    <>
      <p>
        For fixable vulnerabilities vuln-sentinel opens <strong>one draft PR per repo</strong>,
        branch <code>vuln-sentinel/fix-…</code>, covering all current vulnerabilities across every
        ecosystem (npm, Python, base image). The body lists each CVE, the changes, a{' '}
        <code>## Validation</code> section and a <code>## Lockfile</code> section.
      </p>
      <Flow
        label="PR lifecycle"
        nodes={[
          { title: 'Draft PR', sub: 'opened by the agent', tone: 'secondary' },
          { title: 'Your CI runs', sub: 'on the branch', tone: 'info' },
          { title: 'Ready for review', sub: 'when CI is green', tone: 'success' },
          { title: 'You merge', tone: 'primary' },
        ]}
        caption="New CVEs found while the PR is open are added to it as a follow-up commit."
      />

      <h5 className="mt-4">Why one PR per repo, not one per package</h5>
      <ol className="small">
        <li>
          <strong>Whole-repo vulnerability gates.</strong> Many pipelines fail the build if any
          vulnerability is present. A per-package PR still has the others outstanding, so its build
          fails and it can never merge. Only a PR that fixes everything at once can go green.
        </li>
        <li>
          <strong>CI concurrency limits.</strong> Build farms cap parallel builds (AppVeyor allows
          10). A dozen branches would starve the queue.
        </li>
        <li>
          <strong>One lockfile reconcile.</strong> Ten PRs means ten reconciles, reviews and merges.
        </li>
      </ol>
      <p className="small">
        The tradeoff is that one risky bump can hold up the rest. If a specific bump is problematic,
        rework that one change on the branch or peel it off as an exception. A blocked sub-change is
        a local edit; a per-package world is globally unmergeable on a whole-repo gate.
      </p>

      <h5 className="mt-4">The lockfile step</h5>
      <p>
        For Node and Python fixes vuln-sentinel edits the <strong>manifest only</strong>. The PR
        gives the one command to run (<code>npm install</code>, <code>poetry lock</code>…). Commit
        and push the result.
      </p>
      <Why>
        A correct lockfile can only be produced by your repo’s exact toolchain and registry
        (including private ones), which live in your CI or on a developer machine. Generating it
        elsewhere risks a lockfile that resolves differently than your build expects. A stale
        lockfile is what makes <code>npm ci</code> fail, so this step is what actually lands the fix.
      </Why>

      <h5 className="mt-4">Exception PRs</h5>
      <p>
        When there is no safe fix, a <code>vuln-sentinel/exception-…</code> PR adds a documentation
        artifact proposing the risk be accepted. You then file the REQ in ServiceNow and attach it
        (see Exceptions &amp; REQs).
      </p>
    </>
  );
}

/* ------------------------------------------------------------------ Learning */
function Learning() {
  return (
    <>
      <p>
        vuln-sentinel learns from you. If a fix was wrong, incomplete or could have been done better,
        tell it, and it adjusts on future runs for your repo.
      </p>
      <Flow
        label="Feedback loop"
        nodes={[
          { title: 'You comment', sub: 'on the PR or in the app', tone: 'primary' },
          { title: 'Distilled', sub: 'roughly hourly, into a lesson', tone: 'info' },
          { title: 'Learned context', sub: 'scoped to CVE / component / repo', tone: 'success' },
          { title: 'Next run', sub: 'loads relevant lessons', tone: 'primary' },
        ]}
      />
      <h5 className="mt-4">Giving feedback</h5>
      <ul className="small">
        <li>
          On the PR: <code>@vuln-sentinel feedback &lt;verdict&gt; &lt;your note&gt;</code>
        </li>
        <li>In the app: the PR and exception pages have a feedback control.</li>
      </ul>
      <p className="small">
        A bare thumbs-down teaches little. A short <em>why</em> (“override ignored, needed a direct
        bump of X”) becomes a real lesson.
      </p>

      <h5 className="mt-4">How the agent’s context is layered</h5>
      <Layers
        label="Context layers, most specific first"
        layers={[
          { title: 'Learned context', sub: 'distilled from your feedback', badge: 'per repo', tone: 'success' },
          { title: 'Team repo context', sub: 'your ai_context folder', badge: 'per repo', tone: 'info' },
          { title: 'Global guidance', sub: 'shared rules and playbooks', badge: 'everyone', tone: 'secondary' },
        ]}
        caption="Lessons add or clarify; they never override a guardrail."
      />
      <Why>
        Lessons are <strong>per repo</strong>: one team’s feedback never changes another team’s PRs.
        If a lesson is true for everyone, a maintainer can fold it into global guidance, which is a
        deliberate reviewed step, not automatic. Guardrails are never relaxed by feedback (no
        lockfile edits, human-merge-only, no pushing to the default branch).
      </Why>
      <p className="small">
        The repo’s <strong>Learned context</strong> tab lists the lessons in force and lets you
        retire one so it stops loading. That is the “this lesson is wrong” escape hatch.
      </p>
    </>
  );
}

/* ------------------------------------------------------------------ Activity & Why */
function Activity() {
  const { expiryDays: EXPIRY_DAYS } = usePolicy();
  return (
    <>
      <h5>Activity</h5>
      <p>
        The Activity page (and each repo’s Activity tab) records every state-changing action:
        onboarding, config changes (with a before/after view), exception attach / remove / rename /
        renew / retire, feedback. It is the audit trail answering “who changed what, and when”.
      </p>
      <Why>
        Exceptions are compliance records. Every change to one needs an accountable actor and a
        reason that can be reconstructed later.
      </Why>

      <h5 className="mt-4">Design choices at a glance</h5>
      <Table size="sm" bordered>
        <thead>
          <tr>
            <th>Choice</th>
            <th>Why</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Human always merges', 'vuln-sentinel proposes, you decide. Trust and safety.'],
            ['Edit manifest, not lockfile', 'A correct lockfile needs your toolchain and registry.'],
            ['One fix PR per repo', 'Whole-repo CI gates, build concurrency limits, one lockfile reconcile.'],
            ['Per-component exceptions', 'One request covers a component’s CVEs; the response is identical.'],
            ['Stack-pointer onboarding', 'Ask for the stable thing (stacks), derive the changing thing (resources).'],
            ['Pull latest findings', 'Event-driven ingest alone is blind to debt that existed before onboarding.'],
            ['Distinct CVEs on the latest build', 'Inspector repeats a CVE per image digest; raw counts are not actionable.'],
            [`${EXPIRY_DAYS}-day REQ window from filing`, 'Mirrors ServiceNow policy; nothing is amended, only renewed.'],
            ['Edit vs Renew', 'Renaming a ticket must not reset its clock; a new ticket gets its own.'],
            ['Retire only when cleared', 'Prevents silently uncovering a CVE; use Remove for a mistaken attach.'],
            ['Feedback becomes per-repo lessons', 'The agent improves from real reviewer signal without cross-team bleed.'],
          ].map(([c, w]) => (
            <tr key={c}>
              <td className="small fw-semibold">{c}</td>
              <td className="small">{w}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

const TABS: { key: string; title: string; render: () => ReactNode }[] = [
  { key: 'overview', title: 'Overview', render: () => <Overview /> },
  { key: 'repos', title: 'Repos & onboarding', render: () => <Repos /> },
  { key: 'findings', title: 'Findings', render: () => <Findings /> },
  { key: 'exceptions', title: 'Exceptions & REQs', render: () => <Exceptions /> },
  { key: 'prs', title: 'Pull requests', render: () => <Prs /> },
  { key: 'learning', title: 'Feedback & learning', render: () => <Learning /> },
  { key: 'activity', title: 'Activity & design', render: () => <Activity /> },
];

export default function Guide() {
  const [params, setParams] = useSearchParams();
  const policy = useAsync(() => exceptionsService.getExceptionPolicy(), []);
  const requested = params.get('tab');
  const active = TABS.find((t) => t.key === requested) ?? TABS[0];

  return (
    <div style={{ maxWidth: 960 }}>
      <h3>User guide</h3>
      <p className="text-muted">
        What each part of vuln-sentinel does, how it behaves, and why it exists.
      </p>
      <Nav
        variant="tabs"
        activeKey={active.key}
        onSelect={(k) => k && setParams({ tab: k }, { replace: true })}
        className="mb-3"
      >
        {TABS.map((t) => (
          <Nav.Item key={t.key}>
            <Nav.Link eventKey={t.key}>{t.title}</Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      <PolicyContext.Provider value={policy.data ?? FALLBACK_POLICY}>
        <div role="region" aria-label={active.title}>
          {active.render()}
        </div>
      </PolicyContext.Provider>
    </div>
  );
}
